import React, { useMemo } from 'react';
import type { AIAgentCapabilityConfig, AIAgentResponse, ToolPrecondition } from '@troia-v3/types';

import { useCustomActions } from '@/modules/automation/custom-actions/hooks';
import { useWorkflows } from '@/modules/workflows/hooks';
import { TOOL_OPTIONS } from '../../constants/toolLabels';
import { ToolPreconditionsList, type ToolCategory } from './ToolPreconditionsList';
import { SectionCard } from '../SectionCard';

// ─── Mapeamento capability → tool IDs ────────────────────────────────────────
// Sincronizado com o backend (mastra/tools).

const CAPABILITY_TOOL_IDS: Record<string, string[]> = {
  text_generation: [
    'send_text_message',
    'send_audio_message',
    'send_media_message',
    'send_template',
  ],
  calendar_management: [
    'check_calendar_availability',
    'schedule_meeting',
    'update_scheduled_meeting',
    'cancel_scheduled_meeting',
  ],
  lead_management: ['create_lead', 'update_lead'],
  conversation_transfer: ['transfer_to_human', 'transfer_to_team'],
  media_messaging: ['send_media_message'],
  contact_tag_management: ['add_tags', 'remove_tags', 'update_contact_personal_info'],
  // busca em knowledge bases — disponível sempre que o agente tiver databases
  // (não exige capability específica no formato atual)
};

// ─── Props ───────────────────────────────────────────────────────────────────

interface PreconditionsTabProps {
  agent: AIAgentResponse;
  formData: {
    toolPreconditions?: ToolPrecondition[];
    customActionIds?: string[];
    skillWorkflowIds?: string[];
    enabledCapabilities?: AIAgentCapabilityConfig[];
    databases?: string[];
  };
  onChange: (toolPreconditions: ToolPrecondition[]) => void;
}

// ─── Componente ──────────────────────────────────────────────────────────────

export const PreconditionsTab: React.FC<PreconditionsTabProps> = ({
  agent,
  formData,
  onChange,
}) => {
  // Buscar nomes das custom actions (condicionado a ter IDs)
  const customActionIds = formData.customActionIds ?? agent.customActionIds ?? [];
  const { data: customActionsData } = useCustomActions(
    customActionIds.length > 0 ? { status: 'active' } : undefined,
  );

  // Buscar nomes dos workflows skill
  const skillWorkflowIds = formData.skillWorkflowIds ?? agent.skillWorkflowIds ?? [];
  const { data: workflowsData } = useWorkflows(
    skillWorkflowIds.length > 0 ? { status: 'active' } : undefined,
  );

  // ─── Computar categorias de tools disponíveis ──────────────────────────
  const categories = useMemo<ToolCategory[]>(() => {
    const enabledCapabilities = formData.enabledCapabilities ?? agent.enabledCapabilities ?? [];
    const enabledSet = new Set(
      enabledCapabilities.filter((c) => c.enabled).map((c) => c.capability),
    );

    // Built-in: derivado das capabilities habilitadas
    const builtInToolIds = new Set<string>();
    enabledSet.forEach((cap) => {
      const ids = CAPABILITY_TOOL_IDS[cap];
      if (ids) ids.forEach((id) => builtInToolIds.add(id));
    });

    // Sempre incluir as tools de busca se o agente tiver databases configurados
    const databases = formData.databases ?? (agent.databases ?? []);
    if (databases.length > 0) {
      [
        'search_documents',
        'search_products',
        'search_services',
        'search_properties',
        'search_vehicles',
      ].forEach((id) => builtInToolIds.add(id));
    }

    const builtInTools = [...builtInToolIds].map((id) => {
      const opt = TOOL_OPTIONS.find((t) => t.value === id);
      return {
        id,
        name: opt?.label ?? id,
        description: opt?.description,
      };
    });

    // Custom actions — customActionsData é PaginatedResponse cujo array fica em .data
    const customActions = customActionIds
      .map((id) => {
        const action = customActionsData?.data?.find((a) => a.id === id);
        return {
          id,
          name: action?.displayName ?? action?.name ?? id,
          description: action?.description,
        };
      });

    // Workflow skills — workflowsData é WorkflowListResponse cujo array fica em .items
    const skillWorkflows = skillWorkflowIds
      .map((id) => {
        const wf = workflowsData?.items?.find((w) => w.id === id);
        return {
          id: `skill_${id}`,
          name: wf?.name ?? `Skill ${id}`,
          description: wf?.description,
        };
      });

    return [
      { label: 'Ferramentas integradas', tools: builtInTools },
      { label: 'Ações customizadas', tools: customActions },
      { label: 'Skills (Workflows)', tools: skillWorkflows },
    ];
  }, [
    formData.enabledCapabilities,
    formData.customActionIds,
    formData.skillWorkflowIds,
    formData.databases,
    agent.enabledCapabilities,
    agent.customActionIds,
    agent.skillWorkflowIds,
    agent.databases,
    customActionsData,
    workflowsData,
    customActionIds,
    skillWorkflowIds,
  ]);

  const toolPreconditions = formData.toolPreconditions ?? agent.toolPreconditions ?? [];

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Card de seção do mockup agent-form (header tile DENTRO do card) */}
      <SectionCard
        icon="filter"
        title="Pré-condições por Ferramenta"
        subtitle="O agente só chama a ferramenta quando todas as condições configuradas forem atendidas"
      >
        <ToolPreconditionsList
          categories={categories}
          toolPreconditions={toolPreconditions}
          onChange={onChange}
        />
      </SectionCard>
    </div>
  );
};
